import { useState, useCallback, useRef, useEffect } from 'react';

import { GRUNTWORX_REQUEST_EXPORT_ENDPOINTS } from '../../../../constants/api';

// Types
export interface ExportStatus {
  export_id?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  success?: boolean;
}

export interface ExportState {
  isExporting: boolean;
  exportStatus: ExportStatus | null;
  isPolling: boolean;
  pollAttempts: number;
  showStatusDialog: boolean;
}

export interface GruntWorxExportStartResponse {
  success: boolean;
  export_id: string;
  message?: string;
}

// Constants
const EXPORT_CONFIG = {
  MAX_POLL_ATTEMPTS: 10,
  POLL_INTERVAL_PENDING: 5000,
  POLL_INTERVAL_PROCESSING: 2000,
  POLL_INTERVAL_ERROR: 10000,
} as const;

/**
 * Generated-client calls that start a GruntWorx export for one request and read
 * its status. Workflow requests and RFI requests use different endpoints.
 */
export interface GruntWorxExportEndpoints {
  export: (requestUuid: string) => Promise<GruntWorxExportStartResponse>;
  status: (exportId: string, requestUuid: string) => Promise<ExportStatus>;
}

// customApiClient throws the response body for HTTP errors, not an Error.
const errorBodyMessage = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('message' in error)) {
    return undefined;
  }
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && message ? message : undefined;
};

export const useGruntWorxExport = (
  endpoints: GruntWorxExportEndpoints = GRUNTWORX_REQUEST_EXPORT_ENDPOINTS
) => {
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [state, setState] = useState<ExportState>({
    isExporting: false,
    exportStatus: null,
    isPolling: false,
    pollAttempts: 0,
    showStatusDialog: false,
  });

  // Cleanup effect to track component mount status and clear timeouts
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const updateState = useCallback((updates: Partial<ExportState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const clearCurrentTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const safeSetTimeout = useCallback((callback: () => void, delay: number) => {
    clearCurrentTimeout();
    if (isMountedRef.current) {
      timeoutRef.current = setTimeout(callback, delay);
    }
  }, [clearCurrentTimeout]);

  const resetExportState = useCallback(() => {
    clearCurrentTimeout();
    setState({
      isExporting: false,
      exportStatus: null,
      isPolling: false,
      pollAttempts: 0,
      showStatusDialog: false,
    });
  }, [clearCurrentTimeout]);

  const pollExportStatus = useCallback(async (exportId: string, requestUuid: string, currentAttempts: number = 0): Promise<void> => {
    // Early exit if component is unmounted
    if (!isMountedRef.current) return;
    
    if (currentAttempts >= EXPORT_CONFIG.MAX_POLL_ATTEMPTS) {
      const timeoutStatus: ExportStatus = {
        export_id: exportId,
        status: 'failed',
        message: 'Export timed out. Please check your GruntWorx dashboard.',
        success: false
      };
      
      setState(prev => ({
        ...prev,
        exportStatus: timeoutStatus,
        isPolling: false,
        isExporting: false,
        showStatusDialog: true,
      }));
      return;
    }

    try {
      const status = await endpoints.status(exportId, requestUuid);
      const nextAttempts = currentAttempts + 1;
      
      // Check if component is still mounted after async operation
      if (!isMountedRef.current) return;
      
      setState(prev => ({ 
        ...prev,
        exportStatus: status,
        pollAttempts: nextAttempts,
      }));

      switch (status.status) {
        case 'completed':
          setState(prev => ({
            ...prev,
            isPolling: false,
            isExporting: false,
            showStatusDialog: true,
          }));
          break;
        
        case 'failed':
          setState(prev => ({
            ...prev,
            isPolling: false,
            isExporting: false,
            showStatusDialog: true,
          }));
          break;
        
        case 'processing':
          safeSetTimeout(() => pollExportStatus(exportId, requestUuid, nextAttempts), EXPORT_CONFIG.POLL_INTERVAL_PROCESSING);
          break;
        
        case 'pending':
        default:
          safeSetTimeout(() => pollExportStatus(exportId, requestUuid, nextAttempts), EXPORT_CONFIG.POLL_INTERVAL_PENDING);
          break;
      }
    } catch (error) {
      console.error('Failed to poll export status:', error);
      const nextAttempts = currentAttempts + 1;
      
      // Check if component is still mounted after error
      if (!isMountedRef.current) return;
      
      setState(prev => ({ ...prev, pollAttempts: nextAttempts }));
      
      if (nextAttempts < EXPORT_CONFIG.MAX_POLL_ATTEMPTS) {
        safeSetTimeout(() => pollExportStatus(exportId, requestUuid, nextAttempts), EXPORT_CONFIG.POLL_INTERVAL_ERROR);
      } else {
        const errorStatus: ExportStatus = {
          export_id: exportId,
          status: 'failed',
          message: 'Failed to check export status. Please check your GruntWorx dashboard.',
          success: false
        };
        
        setState(prev => ({
          ...prev,
          exportStatus: errorStatus,
          isPolling: false,
          isExporting: false,
          showStatusDialog: true,
        }));
      }
    }
  }, [endpoints, safeSetTimeout]);

  const startExport = useCallback(async (requestUuid: string): Promise<void> => {
    updateState({
      isExporting: true,
      exportStatus: null,
      pollAttempts: 0,
      showStatusDialog: true,
    });
    
    try {
      const result = await endpoints.export(requestUuid);

      if (result.success && result.export_id) {
        const initialStatus: ExportStatus = {
          export_id: result.export_id,
          status: 'pending',
          message: 'GruntWorx export started.'
        };
        
        updateState({
          exportStatus: initialStatus,
          isPolling: true,
        });
        
        // Start polling immediately - pass requestUuid for RFI status endpoint
        pollExportStatus(result.export_id, requestUuid, 0);
      } else {
        throw new Error(result.message || 'Failed to start export');
      }
    } catch (error) {
      console.error('GruntWorx export failed:', error);
      
      let errorMessage = 'Failed to export to GruntWorx. Please try again.';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Export was cancelled.';
        } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message) {
          errorMessage = `Export error: ${error.message}`;
        }
      } else if (errorBodyMessage(error)) {
        errorMessage = `Export error: ${errorBodyMessage(error)}`;
      }
      
      const errorStatus: ExportStatus = {
        status: 'failed',
        message: errorMessage,
        success: false
      };
      
      updateState({
        exportStatus: errorStatus,
        isExporting: false,
        isPolling: false,
      });
    }
  }, [endpoints, updateState, pollExportStatus]);

  const closeStatusDialog = useCallback(() => {
    updateState({ showStatusDialog: false });
  }, [updateState]);

  const retryExport = useCallback((requestUuid: string) => {
    resetExportState();
    startExport(requestUuid);
  }, [resetExportState, startExport]);

  return {
    // State
    ...state,
    maxPollAttempts: EXPORT_CONFIG.MAX_POLL_ATTEMPTS,
    
    // Actions
    startExport,
    closeStatusDialog,
    retryExport,
    resetExportState,
  };
};